import React, { useMemo } from 'react'
import { calculateSubtotal } from 'src/public/components/online_ordering/modifierUtils'
import { useCompletedOrder } from 'src/public/components/online_ordering/OrderContext'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import { t } from 'src/translations/src'
import { useCompletedOrderFreeDelivery } from '../../offers/useCompletedOrderFreeDelivery'
import { splitFreeDeliveryCredit } from '../../offers/useFreeDeliveryOffer'
import { calculateDiscountsTotal } from '../cart/cartUtils'
import DiscountedPrice from '../order/DiscountedPrice'
import OrderItems, { OrderPrices, ECOMM_ITEM_NAME } from '../order/OrderItems'
import { PackagingSelections } from './PackagingSelections/PackagingSelections'

export const OrderSummarySection = () => {
  const { completedOrder, loadingCompletedOrder } = useCompletedOrder()
  const freeDelivery = useCompletedOrderFreeDelivery()
  const subtotal = useMemo(
    () => calculateSubtotal(completedOrder),
    [completedOrder]
  )

  const deliveryFee = completedOrder?.deliveryServiceCharges ?? 0
  const {
    coveredByDeliveryFee,
    uncoveredCredit,
    discountedDeliveryFee,
    isDeliveryFeeDiscounted
  } = splitFreeDeliveryCredit(freeDelivery.amount, deliveryFee)

  const orderTotal = completedOrder
    ? completedOrder.totalV2 -
      (completedOrder.giftCard?.appliedBalance ?? 0) -
      (freeDelivery.isFundedOfferInTotal ? freeDelivery.amount : 0)
    : 0
  // Only the struck-through delivery fee is added back: the uncovered part of the
  // credit stays visible in Discounts, exactly as it does at checkout.
  const preOfferTotal = orderTotal + coveredByDeliveryFee

  const guestPayment = completedOrder?.payments.find(
    (payment) => !freeDelivery.fundedOfferPaymentGuids.has(payment.guid)
  )

  const orderDate = useMemo(() => {
    return new Date(completedOrder?.createdDateTime ?? 0).toLocaleString([], {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }, [completedOrder?.createdDateTime])

  if (!completedOrder) return null

  return (
    <>
      <div
        className='orderConfirmationSection mediumPadding leftAlign summarySection'
        data-testid='order-summary-section'
        role='group'
        tabIndex={0}
      >
        <div className='orderSummaryHeader'>
          <h2>{t('order-confirmation.order-summary')}</h2>
          {completedOrder.checkNumber && (
            <div className='checkNumber'>
              {t('order-confirmation.order-summary.check-number', {
                checkNumber: `${completedOrder.checkNumber}`.padStart(5, '0')
              })}
            </div>
          )}
        </div>
        <div className='cartOrder'>
          <div className='cartData'>
            <OrderItems selections={completedOrder.selections} />
            {Boolean(completedOrder.packagingSelections.length) && (
              <PackagingSelections
                packaging={completedOrder.packagingSelections || []}
              />
            )}
            <OrderPrices
              loading={loadingCompletedOrder}
              subtotal={subtotal}
              discountsTotal={calculateDiscountsTotal(
                completedOrder?.discounts?.globalReward?.amount ?? 0,
                (completedOrder?.discounts?.boostDiscount?.amount ?? 0) +
                  uncoveredCredit,
                completedOrder.discountsTotal
              )}
              diningOptionBehavior={completedOrder.diningOptionBehavior}
              deliveryFee={discountedDeliveryFee}
              originalDeliveryFee={
                isDeliveryFeeDiscounted ? deliveryFee : undefined
              }
              gratuityServiceCharges={completedOrder.gratuityServiceCharges}
              processingServiceCharges={completedOrder.processingServiceCharges}
              nonDeliveryNonGratuityNonUbpServiceCharges={
                completedOrder.nonDeliveryNonGratuityNonUbpServiceCharges
              }
              tax={completedOrder.taxV2}
              tip={completedOrder.tip}
              fundraisingAmount={completedOrder.fundraisingTotal}
              giftCardAppliedAmount={completedOrder.giftCard?.appliedBalance}
              surchargeAmount={
                completedOrder.surchargeServiceCharges.baseAmount
              }
              surchargeTaxAmount={
                completedOrder.surchargeServiceCharges.taxAmount
              }
              shipping={
                completedOrder.selections.find(
                  (selection) => selection.name === ECOMM_ITEM_NAME
                )?.price
              }
              enableTooltip={false}
            />
            <div className='prices totalPriceContainer'>
              <div className='cart-flex-row'>
                <div className='totalPrice'>
                  {t('order-confirmation.total')}
                </div>
                <div className='totalPrice'>
                  {isDeliveryFeeDiscounted ? (
                    <DiscountedPrice
                      originalAmount={preOfferTotal}
                      amount={orderTotal}
                      testIdPrefix='order-total'
                    />
                  ) : (
                    <FormattedPrice value={orderTotal} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className='orderPlacedContainer mediumPadding'
        role='group'
        aria-label={t('order-confirmation.ordered')}
        tabIndex={0}
      >
        <div className='cart-flex-row'>
          <div className='label'>{t('order-confirmation.ordered')}</div>
          <div>{orderDate}</div>
        </div>
        {/* One guest payment via ca-bff logic, plus any Toast-funded credit  */}
        {guestPayment && (
          <>
            {guestPayment.last4Digits && (
              <div className='cart-flex-row'>
                <div className='label'>{guestPayment.cardType}</div>
                <div className='label'>
                  <span>xxxxxxxxxxxx{guestPayment.last4Digits}</span>
                </div>
              </div>
            )}
            <div className='transactionDetails'>
              <div className='cart-flex-row'>
                <div className='label'>
                  {t('order-confirmation.transaction-type')}
                </div>
                <div className='label'>{guestPayment.transactionType}</div>
              </div>
              <div className='cart-flex-row'>
                <div className='label'>
                  {t('order-confirmation.authorization')}
                </div>
                <div className='label'>{guestPayment.authorizationStatus}</div>
              </div>
              <div className='cart-flex-row'>
                <div className='label'>
                  {t('order-confirmation.approval-code')}
                </div>
                <div className='label'>{guestPayment.approvalCode}</div>
              </div>
              <div className='cart-flex-row'>
                <div className='label'>
                  {t('order-confirmation.payment-id')}
                </div>
                <div className='label'>{guestPayment.paymentId}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
